import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, Wallet, PlayCircle, Timer, Users2, Sparkles, Info,
  CheckCircle2, Clock, XCircle, ArrowRightCircle, Loader2,
} from "lucide-react";
import BankAccountForm from "../components/BankAccountForm";

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;
const fmtMinutes = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} min`;
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-").map(Number);
  return new Date(y, mm - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
};

export default function CreatorRevenue() {
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [e, p] = await Promise.all([
        api.get("/creator/earnings").then((r) => r.data),
        api.get("/creator/payouts").then((r) => r.data).catch(() => []),
      ]);
      setData(e);
      setPayouts(p || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="revenue-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#E65F2B]" />
      </div>
    );
  }

  const { config, current, history, bank, lifetime_earned_ngn, lifetime_paid_ngn, carry_balance_ngn } = data;
  const currentStatus = current?.status || "open";
  const statusPill = (s) => {
    if (s === "paid") return "bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/25";
    if (s === "eligible") return "bg-[#EAB308]/10 text-[#EAB308] border border-[#EAB308]/25";
    if (s === "carried_forward") return "bg-[#3B82F6]/10 text-[#60A5FA] border border-[#3B82F6]/25";
    return "bg-white/5 text-white/50 border border-white/10";
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="creator-revenue-page">
      <Link to="/creator" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white mb-6" data-testid="back-to-creator">
        <ArrowLeft className="h-4 w-4" /> Back to Studio
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-10">
        <div>
          <p className="text-eyebrow">Commissions & Payouts</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
            Your <span className="text-[#E65F2B]">earnings</span>
          </h1>
          <p className="text-white/50 mt-2 text-sm max-w-2xl">
            60% of matured subscription revenue is shared with creators every month, split across{" "}
            <b className="text-white/80">streams</b>, <b className="text-white/80">watch-time</b> and{" "}
            <b className="text-white/80">unique listeners</b>. Payments land in your NUBAN account once
            your balance clears {fmt(config.min_payout_ngn)}.
          </p>
        </div>
        <Link to="/how-payouts-work" className="ghost-btn shrink-0" data-testid="link-transparency">
          Public transparency page →
        </Link>
      </div>

      {/* Current month hero */}
      <div className="card-surface p-6 sm:p-8 mb-8 border-[#E65F2B]/25" data-testid="current-month-card">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
          <div>
            <p className="text-eyebrow text-[#E65F2B]">This month · {monthLabel(current?.month)}</p>
            <p className="font-display text-3xl sm:text-4xl font-black mt-1" data-testid="current-available">
              {fmt(current?.available_ngn)}
            </p>
          </div>
          <span className={`badge-pill text-[10px] ${statusPill(currentStatus)}`} data-testid="current-status-pill">
            {currentStatus === "paid" ? "PAID" :
             currentStatus === "eligible" ? "READY FOR PAYOUT" :
             currentStatus === "carried_forward" ? "CARRIED FORWARD" : "IN PROGRESS"}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5" data-testid="metric-streams">
            <div className="flex items-center gap-2 mb-2">
              <PlayCircle className="h-4 w-4 text-[#E65F2B]" />
              <p className="text-eyebrow">Streams · SBC (25%)</p>
            </div>
            <p className="font-display text-2xl font-black tabular-nums">{Number(current?.streams || 0).toLocaleString()}</p>
            <p className="text-xs text-white/40 mt-1">= {fmt(current?.sbc_earning_ngn)}</p>
          </div>
          <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5" data-testid="metric-duration">
            <div className="flex items-center gap-2 mb-2">
              <Timer className="h-4 w-4 text-[#EAB308]" />
              <p className="text-eyebrow">Watch-time · DBC (20%)</p>
            </div>
            <p className="font-display text-2xl font-black tabular-nums">{fmtMinutes(current?.duration_minutes)}</p>
            <p className="text-xs text-white/40 mt-1">= {fmt(current?.dbc_earning_ngn)}</p>
          </div>
          <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5" data-testid="metric-listeners">
            <div className="flex items-center gap-2 mb-2">
              <Users2 className="h-4 w-4 text-[#60A5FA]" />
              <p className="text-eyebrow">Unique listeners · LBC (15%)</p>
            </div>
            <p className="font-display text-2xl font-black tabular-nums">{Number(current?.unique_listeners || 0).toLocaleString()}</p>
            <p className="text-xs text-white/40 mt-1">= {fmt(current?.lbc_earning_ngn)}</p>
          </div>
        </div>

        {(current?.carry_from_previous_ngn > 0) && (
          <div className="mt-5 p-3 rounded-lg bg-[#3B82F6]/5 border border-[#3B82F6]/25 flex items-center gap-2" data-testid="carry-banner">
            <ArrowRightCircle className="h-4 w-4 text-[#60A5FA] shrink-0" />
            <p className="text-sm text-white/70">
              Includes <b className="text-[#60A5FA]">{fmt(current.carry_from_previous_ngn)}</b> carried forward from last month.
            </p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-white/40">
          <Info className="h-3.5 w-3.5" />
          <span>Live estimate. Only matured (≥{config.maturation_hours}h old) paid subscriptions count. Streams need ≥{config.stream_qualify_seconds}s watch-time to qualify.</span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <div className="card-surface p-5" data-testid="kpi-lifetime-earned">
          <Sparkles className="h-4 w-4 text-[#E65F2B] mb-3" />
          <p className="text-eyebrow">Lifetime earned</p>
          <p className="font-display text-2xl font-black mt-1">{fmt(lifetime_earned_ngn)}</p>
        </div>
        <div className="card-surface p-5" data-testid="kpi-lifetime-paid">
          <CheckCircle2 className="h-4 w-4 text-[#22C55E] mb-3" />
          <p className="text-eyebrow">Paid out</p>
          <p className="font-display text-2xl font-black mt-1">{fmt(lifetime_paid_ngn)}</p>
        </div>
        <div className="card-surface p-5" data-testid="kpi-carry">
          <ArrowRightCircle className="h-4 w-4 text-[#60A5FA] mb-3" />
          <p className="text-eyebrow">Carry balance</p>
          <p className="font-display text-2xl font-black mt-1">{fmt(carry_balance_ngn)}</p>
        </div>
        <div className="card-surface p-5" data-testid="kpi-min-payout">
          <Wallet className="h-4 w-4 text-[#EAB308] mb-3" />
          <p className="text-eyebrow">Payout threshold</p>
          <p className="font-display text-2xl font-black mt-1">{fmt(config.min_payout_ngn)}</p>
          <p className="text-xs text-white/40 mt-1">Auto-carries below this</p>
        </div>
      </div>

      {/* Bank account */}
      <h2 className="font-display text-2xl font-bold mb-4">Payout details</h2>
      <div className="mb-10">
        <BankAccountForm current={bank} onSaved={load} />
      </div>

      {/* History */}
      <h2 className="font-display text-2xl font-bold mb-4">Monthly history</h2>
      {history.length === 0 ? (
        <div className="card-surface p-10 text-center text-white/50" data-testid="no-history">
          No completed months yet. Your first roster will appear after {monthLabel(current?.month)} closes.
        </div>
      ) : (
        <div className="card-surface overflow-hidden" data-testid="history-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-left">
                <th className="px-5 py-3 text-eyebrow">Month</th>
                <th className="px-5 py-3 text-eyebrow text-right">Streams</th>
                <th className="px-5 py-3 text-eyebrow text-right">Minutes</th>
                <th className="px-5 py-3 text-eyebrow text-right">Listeners</th>
                <th className="px-5 py-3 text-eyebrow text-right">Earned</th>
                <th className="px-5 py-3 text-eyebrow text-right">Payout</th>
                <th className="px-5 py-3 text-eyebrow">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.month} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`history-row-${row.month}`}>
                  <td className="px-5 py-4 font-display font-bold">{monthLabel(row.month)}</td>
                  <td className="px-5 py-4 text-right tabular-nums">{row.streams.toLocaleString()}</td>
                  <td className="px-5 py-4 text-right tabular-nums">{Number(row.duration_minutes).toFixed(1)}</td>
                  <td className="px-5 py-4 text-right tabular-nums">{row.unique_listeners.toLocaleString()}</td>
                  <td className="px-5 py-4 text-right tabular-nums text-white/70">{fmt(row.subtotal_ngn)}</td>
                  <td className="px-5 py-4 text-right tabular-nums font-display font-bold text-[#EAB308]">{fmt(row.available_ngn)}</td>
                  <td className="px-5 py-4">
                    <span className={`badge-pill text-[10px] ${statusPill(row.status)}`}>
                      {row.status.replace("_", " ")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payouts */}
      <h2 className="font-display text-2xl font-bold mt-12 mb-4">Payout transactions</h2>
      {payouts.length === 0 ? (
        <div className="card-surface p-10 text-center text-white/50" data-testid="no-payouts">
          No payouts yet. When your monthly total clears {fmt(config.min_payout_ngn)}, we send it automatically.
        </div>
      ) : (
        <div className="space-y-3" data-testid="payouts-list">
          {payouts.map((p) => (
            <div key={p.id} className="card-surface p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`payout-${p.id}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-bold text-lg">{fmt(p.amount_ngn)}</p>
                  <span className="text-xs text-white/40">·</span>
                  <p className="text-sm text-white/60">{monthLabel(p.month)}</p>
                  <span className={`badge-pill text-[10px] ${
                    p.status === "success" ? "bg-[#22C55E]/10 text-[#22C55E] border border-[#22C55E]/25" :
                    p.status === "failed" || p.status === "reversed" ? "bg-red-500/10 text-red-400 border border-red-500/25" :
                    "bg-[#EAB308]/10 text-[#EAB308] border border-[#EAB308]/25"
                  }`}>
                    {p.status === "success" ? <><CheckCircle2 className="h-3 w-3 mr-1 inline" /> paid</> :
                     p.status === "failed" || p.status === "reversed" ? <><XCircle className="h-3 w-3 mr-1 inline" /> {p.status}</> :
                     <><Clock className="h-3 w-3 mr-1 inline" /> {p.status}</>}
                  </span>
                </div>
                <p className="text-xs text-white/40 mt-1">
                  {p.bank_name} · ****{(p.account_number || "").slice(-4)} ·
                  {" "}Initiated {new Date(p.created_at).toLocaleDateString()}
                  {p.paid_at ? ` · Settled ${new Date(p.paid_at).toLocaleDateString()}` : ""}
                </p>
                {p.failure_reason && <p className="text-xs text-red-400 mt-1">Reason: {p.failure_reason}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
